import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAdminNotices } from "@/hooks/use-admin-queries";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { PaginationBar, paginate } from "@/components/ui/pagination-bar";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import { formatIST, formatNoticeDateList } from "@/lib/timezone";
import {
  noticeDates,
  noticeRequestedStatus,
  type NoticeApprovalStatus,
  type RequestedStatus,
} from "@/lib/types";

function approvalBadge(
  status: NoticeApprovalStatus,
  requested: RequestedStatus
): { label: string; variant: "default" | "secondary" | "outline" } {
  if (status === "submitted") {
    return { label: "Pending review", variant: "default" };
  }
  if (status === "approved") {
    return { label: `Approved (${requested})`, variant: "secondary" };
  }
  return { label: "Rejected (A)", variant: "outline" };
}

export default function NoticesInboxPage() {
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [query, setQuery] = useState("");
  const queryClient = useQueryClient();
  const adminPassword = getAdminPassword();
  const { data, isLoading, error } = useAdminNotices({
    from: from || undefined,
    to: to || undefined,
  });

  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: "approve" | "reject";
    }) => adminApi.decideNotice(adminPassword!, id, decision),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "notices"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "register"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });

  if (isLoading) {
    return <p className="text-muted-foreground">Loading notices…</p>;
  }

  if (error || !data) {
    return <p className="text-red-600">Could not load notices.</p>;
  }

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? data.filter((n) => n.person_name.toLowerCase().includes(needle))
    : data;
  const pending = filtered.filter((n) => n.approval_status === "submitted");
  const { page: safePage, pages, total, slice } = paginate(filtered, page);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">Notices inbox</h2>
        <p className="text-sm text-muted-foreground">
          Review absence notices — approve marks the requested status (O, S, or
          TP) on all selected dates; reject marks{" "}
          <span className="font-semibold text-red-700">A</span> (absent). Filter
          by the leave dates they applied for.
          {pending.length > 0 && ` · ${pending.length} pending`}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-3">
        <div className="min-w-[12rem] flex-1 space-y-1">
          <Label className="text-xs text-muted-foreground">Search name</Label>
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Type a devotee name"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Leave from</Label>
          <DatePicker
            value={from}
            onChange={(v) => {
              setFrom(v);
              setPage(1);
            }}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Leave to</Label>
          <DatePicker
            value={to}
            onChange={(v) => {
              setTo(v);
              setPage(1);
            }}
          />
        </div>
        {(from || to) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setFrom("");
              setTo("");
              setPage(1);
            }}
          >
            Clear dates
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {slice.map((notice) => {
          const requested = noticeRequestedStatus(notice);
          const dates = noticeDates(notice);
          const badge = approvalBadge(notice.approval_status, requested);

          return (
            <Card
              key={notice.id}
              className={
                notice.approval_status === "submitted"
                  ? "border-saffron-200"
                  : "opacity-90"
              }
            >
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-base">{notice.person_name}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="notice">{requested}</Badge>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>
                  <span className="text-muted-foreground">
                    {dates.length > 1 ? "Dates:" : "Date:"}
                  </span>{" "}
                  {formatNoticeDateList(dates)}
                  {dates.length > 1 && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({dates.length} days)
                    </span>
                  )}
                </p>
                {dates.length > 1 && (
                  <div className="flex flex-wrap gap-1">
                    {dates.map((d) => (
                      <Badge key={d} variant="secondary" className="text-xs">
                        {d}
                      </Badge>
                    ))}
                  </div>
                )}
                <p>
                  <span className="text-muted-foreground">Mark as:</span>{" "}
                  {requested === "O"
                    ? "Official leave (O)"
                    : requested === "S"
                      ? "Sick (S)"
                      : "Taken permission (TP)"}
                </p>
                <p>
                  <span className="text-muted-foreground">Session:</span>{" "}
                  {notice.session.replace(/_/g, " ")}
                </p>
                {notice.notes && (
                  <p className="text-muted-foreground">{notice.notes}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Raised {notice.created_at ? formatIST(notice.created_at) : "—"}
                  {notice.reviewed_at &&
                    notice.approval_status !== "submitted" && (
                      <>
                        {" "}
                        · Admin{" "}
                        {notice.approval_status === "approved"
                          ? "approved"
                          : "reviewed"}{" "}
                        {formatIST(notice.reviewed_at)}
                      </>
                    )}
                </p>
                {notice.approval_status === "submitted" && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={() =>
                        decide.mutate({ id: notice.id, decision: "approve" })
                      }
                      disabled={decide.isPending}
                    >
                      Approve ({requested})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        decide.mutate({ id: notice.id, decision: "reject" })
                      }
                      disabled={decide.isPending}
                    >
                      Reject (A)
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <p className="text-muted-foreground">
          {needle ? "No notices match that name." : "No notices submitted yet."}
        </p>
      )}

      <PaginationBar
        page={safePage}
        pages={pages}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
}
